import React, { useMemo, useState, useEffect } from 'react';
import { Chart, Series, ArgumentAxis, ValueAxis, Legend, Tooltip } from 'devextreme-react/chart';
import { CardAnalytics } from '../../../components/library/card-analytics/CardAnalytics';
import { useAppSelector, useAppDispatch } from '../../../contexts/hooks';
import { EReportId, OrdersTrendsRawData } from '../../../types/types.dashboard';
import { useApiDashBoard } from '../../../api/api.dashboards';
import { useScreenSize } from '../../../utils/media-query';
import { FilteredOrders, ResultEstadoResult } from '../../../types/types.orders';
import { setFilteresToOrders } from '../../../contexts/slices/orders';
import { ConvertLocalDate } from '../../../utils/functions';
import { useNavigate } from 'react-router-dom';
import './OrdersTrendsNew.scss';

export const BoardOrdersTrends = () => {
  const { getData } = useApiDashBoard();
  const { isXSmall, isSmall } = useScreenSize();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const dashboard = useAppSelector((x) => x.dashboards.dashboards.find((x) => x.Db_Id === EReportId.OrdersTrends));

  // Estados para controles
  const [selectedPeriod, setSelectedPeriod] = useState<'current' | 'previous'>('current');
  const [selectedDay, setSelectedDay] = useState<string>('Todos');

  // Tipos para los datos del gráfico
  interface ChartDataItem {
    argument: string;
    received: number;
    processed: number;
    pending: number;
  }

  // Función de refresh
  const handleRefreshClick = () => {
    getData(EReportId.OrdersTrends);
  };

  // Función para manejar clicks en métricas
  const handleMetricClick = (metricType: 'received' | 'processed' | 'pending') => {
    let estados: ResultEstadoResult[] = [];

    // Definir estados según el tipo de métrica
    switch (metricType) {
      case 'received':
        estados = []; // Sin filtro de estado - todas las órdenes recibidas
        break;
      case 'processed':
        estados = [ResultEstadoResult.Validado, ResultEstadoResult.Impreso, ResultEstadoResult.OtrasFuentes]; // Estados procesados
        break;
      case 'pending':
        estados = [ResultEstadoResult.SinResultado, ResultEstadoResult.ConResultado]; // Estados pendientes
        break;
    }

    // Calcular rango de fechas basado en la selección actual
    let fechaInicial: string;
    let fechaFinal: string;

    if (selectedDay === 'Todos') {
      // Si está "Todos" seleccionado, usar todo el rango del período
      fechaInicial = processedData.dateRange.from;
      fechaFinal = processedData.dateRange.to;
    } else {
      // Si hay un día específico seleccionado, encontrar la fecha de ese día
      const dayData = availableDays.find((day) => day.dayOfWeek === selectedDay);
      if (dayData) {
        fechaInicial = dayData.date;
        fechaFinal = dayData.date;
      } else {
        // Fallback al rango completo
        fechaInicial = processedData.dateRange.from;
        fechaFinal = processedData.dateRange.to;
      }
    }

    // Crear filtros para órdenes
    const filters: FilteredOrders = {
      Servicios: [],
      Fechas: [ConvertLocalDate(new Date(fechaInicial)), ConvertLocalDate(new Date(fechaFinal))],
      Areas: [],
      Estudios: [],
      Estados: estados,
      Redirect: true,
    };

    dispatch(setFilteresToOrders(filters));
    navigate('/admin-orders'); // Sin state, preserva el tab actual del usuario
  };

  // Obtener días disponibles para el período seleccionado (solo días con datos)
  const availableDays = useMemo(() => {
    if (!dashboard?.Data || !Array.isArray(dashboard.Data)) {
      return [];
    }

    const periodData = dashboard.Data.filter((item: OrdersTrendsRawData) => item.period === selectedPeriod);

    // Solo incluir días que tengan al menos una orden
    const daysWithData = periodData.filter((item) => item.ordersReceived > 0 || item.ordersProcessed > 0 || item.ordersPending > 0);

    // Obtener días únicos con sus fechas para ordenar cronológicamente
    const dayDateMap = new Map<string, { date: string; dayOfWeek: string }>();
    daysWithData.forEach((item) => {
      if (!dayDateMap.has(item.dayOfWeek) || item.date > dayDateMap.get(item.dayOfWeek)!.date) {
        dayDateMap.set(item.dayOfWeek, { date: item.date, dayOfWeek: item.dayOfWeek });
      }
    });

    // Convertir a array y ordenar por fecha (cronológicamente)
    const daysWithDates = Array.from(dayDateMap.entries()).map(([day, data]) => ({
      day,
      date: data.date,
      dayOfWeek: data.dayOfWeek,
    }));
    daysWithDates.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return daysWithDates.map((item) => ({
      dayOfWeek: item.dayOfWeek,
      date: item.date,
    }));
  }, [dashboard?.Data, selectedPeriod]);

  // Resetear día seleccionado cuando cambio de período
  useEffect(() => {
    setSelectedDay('Todos');
  }, [selectedPeriod]);

  // Función para obtener la etiqueta de un día (con "Hoy" y "Ayer")
  const getDayLabel = (dayOfWeek: string, date: string, isSmall: boolean) => {
    const dayDate = new Date(date);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    // Comparar solo año, mes y día (ignorar hora)
    const isToday = dayDate.toDateString() === today.toDateString();
    const isYesterday = dayDate.toDateString() === yesterday.toDateString();

    if (isToday) {
      return 'Hoy';
    } else if (isYesterday) {
      return 'Ayer';
    } else {
      return getDayAbbreviation(dayOfWeek, isSmall);
    }
  };

  // Función para abreviar días
  const getDayAbbreviation = (day: string, isSmall: boolean) => {
    const abbreviations: { [key: string]: { short: string; long: string } } = {
      Lunes: { short: 'Lun', long: 'Lunes' },
      Martes: { short: 'Mar', long: 'Martes' },
      Miércoles: { short: 'Mié', long: 'Miércoles' },
      Jueves: { short: 'Jue', long: 'Jueves' },
      Viernes: { short: 'Vie', long: 'Viernes' },
      Sábado: { short: 'Sáb', long: 'Sábado' },
      Domingo: { short: 'Dom', long: 'Domingo' },
    };

    return isSmall ? abbreviations[day]?.short || day : abbreviations[day]?.long || day;
  };

  // Procesar datos según filtros
  const processedData = useMemo(() => {
    if (!dashboard?.Data || !Array.isArray(dashboard.Data)) {
      return {
        chartData: [],
        dateRange: { from: '', to: '' },
        summary: {
          totalReceived: 0,
          totalProcessed: 0,
          totalPending: 0,
          services: 0,
        },
      };
    }

    let filteredData = dashboard.Data.filter((item: OrdersTrendsRawData) => item.period === selectedPeriod);

    // Filtrar por día si no es "Todos"
    if (selectedDay !== 'Todos') {
      filteredData = filteredData.filter((item: OrdersTrendsRawData) => item.dayOfWeek === selectedDay);
    }

    // Ordenar por fecha
    const sortedData = [...filteredData].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Calcular rango de fechas
    const dateRange = {
      from: sortedData.length > 0 ? sortedData[0].date : '',
      to: sortedData.length > 0 ? sortedData[sortedData.length - 1].date : '',
    };

    // Preparar datos para el gráfico agrupando por fecha
    let chartData: ChartDataItem[] = [];
    let summary = {
      totalReceived: 0,
      totalProcessed: 0,
      totalPending: 0,
      services: 0,
    };

    // Agrupar por fecha
    const dateData = new Map();

    sortedData.forEach((item) => {
      const dateKey = item.date;
      if (!dateData.has(dateKey)) {
        dateData.set(dateKey, {
          date: item.date,
          dayOfWeek: item.dayOfWeek,
          received: 0,
          processed: 0,
          pending: 0,
        });
      }

      const day = dateData.get(dateKey);
      day.received += item.ordersReceived;
      day.processed += item.ordersProcessed;
      day.pending += item.ordersPending;
    });

    chartData = Array.from(dateData.values()).map((day) => {
      const dayDate = new Date(day.date);
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);

      // Comparar solo año, mes y día (ignorar hora)
      const isToday = dayDate.toDateString() === today.toDateString();
      const isYesterday = dayDate.toDateString() === yesterday.toDateString();

      let dateLabel;
      if (isToday) {
        dateLabel = 'Hoy';
      } else if (isYesterday) {
        dateLabel = 'Ayer';
      } else {
        dateLabel = dayDate.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
      }

      return {
        argument: `${getDayAbbreviation(day.dayOfWeek, isXSmall)}\n${dateLabel}`,
        received: day.received,
        processed: day.processed,
        pending: day.pending,
      };
    });

    // Calcular totales
    summary.totalReceived = chartData.reduce((sum, item) => sum + item.received, 0);
    summary.totalProcessed = chartData.reduce((sum, item) => sum + item.processed, 0);
    summary.totalPending = chartData.reduce((sum, item) => sum + item.pending, 0);
    summary.services = new Set(sortedData.map((item) => item.serviceArea)).size;

    return { chartData, dateRange, summary };
  }, [dashboard?.Data, selectedPeriod, selectedDay, isXSmall]);

  // Formatear fechas para mostrar
  const formatDate = (date: string) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <CardAnalytics title='Monitor de Órdenes' contentClass='trends-chart' isLoading={dashboard?.Loading} onRefreshClick={handleRefreshClick}>
      {!dashboard?.Loading && (
        <div className='trends-container'>
          {/* Primera línea: Períodos y métricas */}
          <div className={`trends-header-line ${!isXSmall && !isSmall ? 'desktop-layout' : ''}`}>
            <div className='trends-periods-section'>
              <div className='period-buttons'>
                <button className={`period-btn ${selectedPeriod === 'current' ? 'active' : ''}`} onClick={() => setSelectedPeriod('current')}>
                  Actual: {processedData.dateRange.from && processedData.dateRange.to ? `${formatDate(processedData.dateRange.from)} - ${formatDate(processedData.dateRange.to)}` : 'Sin datos'}
                </button>
                <button className={`period-btn ${selectedPeriod === 'previous' ? 'active' : ''}`} onClick={() => setSelectedPeriod('previous')}>
                  Anterior: {selectedPeriod === 'previous' && processedData.dateRange.from && processedData.dateRange.to ? `${formatDate(processedData.dateRange.from)} - ${formatDate(processedData.dateRange.to)}` : 'Ver fechas'}
                </button>
              </div>
            </div>

            {/* Métricas - posición condicional según pantalla */}
            <div className={`trends-metrics-section ${!isXSmall && !isSmall ? 'horizontal-metrics' : 'vertical-metrics'}`}>
              <button className='metric-item received' onClick={() => handleMetricClick('received')}>
                <span className='metric-number'>{processedData.summary.totalReceived.toLocaleString()}</span>
                <span className='metric-text'>RECIBIDAS</span>
              </button>
              <button className='metric-item processed' onClick={() => handleMetricClick('processed')}>
                <span className='metric-number'>{processedData.summary.totalProcessed.toLocaleString()}</span>
                <span className='metric-text'>PROCESADAS</span>
              </button>
              <button className='metric-item pending' onClick={() => handleMetricClick('pending')}>
                <span className='metric-number'>{processedData.summary.totalPending.toLocaleString()}</span>
                <span className='metric-text'>PENDIENTES</span>
              </button>
            </div>
          </div>

          {/* Segunda línea: Chips de días */}
          {availableDays.length > 0 && (
            <div className='trends-days-line'>
              <div className='days-chips'>
                <button className={`day-chip compact ${selectedDay === 'Todos' ? 'active' : ''}`} onClick={() => setSelectedDay('Todos')}>
                  Todos
                </button>

                {availableDays.map((dayData) => (
                  <button key={dayData.dayOfWeek} className={`day-chip compact ${selectedDay === dayData.dayOfWeek ? 'active' : ''}`} onClick={() => setSelectedDay(dayData.dayOfWeek)}>
                    {getDayLabel(dayData.dayOfWeek, dayData.date, isXSmall || isSmall)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Gráfico expandido */}
          <div className='chart-container-expanded'>
            {processedData.chartData.length > 0 ? (
              <Chart dataSource={processedData.chartData} height={isXSmall ? 180 : isSmall ? 200 : 220} palette={['#4BAAF3', '#28A745', '#FFC107']}>
                <ArgumentAxis
                  discreteAxisDivisionMode='crossLabels'
                  label={{
                    rotationAngle: isXSmall ? -45 : 0,
                    overlappingBehavior: 'rotate',
                  }}
                />
                <ValueAxis
                  label={{                    
                    format: {
                      type: 'decimal',
                      precision: 0,
                    },
                  }}
                />

                <Series valueField='received' argumentField='argument' name='Recibidas' type='bar' color='#4BAAF3' />

                <Series valueField='processed' argumentField='argument' name='Procesadas' type='bar' color='#28A745' />

                <Series valueField='pending' argumentField='argument' name='Pendientes' type='bar' color='#FFC107' />

                <Legend visible={!isXSmall} verticalAlignment='bottom' horizontalAlignment='center' />

                <Tooltip
                  enabled={true}
                  customizeTooltip={(arg) => ({
                    text: `${arg.argument}<br/><strong>${arg.seriesName}</strong>: ${arg.value} órdenes`,
                  })}
                />
              </Chart>
            ) : (
              <div className='no-data'>
                <div className='no-data-icon'>📊</div>
                <div className='no-data-text'>No hay datos para el período seleccionado</div>
              </div>
            )}
          </div>
        </div>
      )}
    </CardAnalytics>
  );
};
